import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { GoalType, ReadingGoalView } from '@/types/api';

/**
 * Wizard navigation states. The progress screen is rendered both for an
 * active goal and a freshly-completed one — the screen reads
 * `goal.status` to decide whether to show the "Done!" banner.
 *
 * The previous "Do you have a reading goal?" screen (`'goal'`) was
 * removed from the wizard. Older clients may still have it cached in
 * localStorage; the persist `migrate` below remaps it to `'bookGoal'`.
 */
export type WizardScreen = 'book' | 'bookGoal' | 'summary' | 'progress';

/**
 * Transient inputs the user has supplied during the wizard, before we
 * call POST /reading-goals to mint the server-confirmed plan. Persisted
 * so a page refresh mid-wizard doesn't lose the draft.
 */
export interface WizardDraft {
  goalType: GoalType | null;
  perDay: number;
}

const EMPTY_DRAFT: WizardDraft = {
  goalType: null,
  perDay: 2,
};

/**
 * ReadingTrackerStore — per-(reader, book) wizard state, plus a cache of
 * server-confirmed goals.
 *
 * Keying on `${readerId}:${bookId}` lets a household with multiple
 * readers each have an independent in-flight wizard for the same book
 * (and lets the same reader hop between books without losing partial
 * input on either side).
 *
 * The store deliberately does NOT own:
 *   - The active reader id (that's `useReadersStore`).
 *   - The book metadata (that's fetched on mount via `booksService.getById`).
 *   - The auth token (that's `useSessionStore`).
 *
 * Persisted to localStorage so a refresh during the wizard survives.
 */
export interface ReadingTrackerState {
  currentScreens: Record<string, WizardScreen>;
  drafts: Record<string, WizardDraft>;
  /**
   * Cache of the active goal per (reader, book) — `null` means we
   * checked and there's nothing active. Distinct from `undefined`
   * (= not yet checked).
   */
  goalsByBook: Record<string, ReadingGoalView | null>;

  setScreen: (key: string, screen: WizardScreen) => void;
  setDraft: (key: string, patch: Partial<WizardDraft>) => void;
  resetDraft: (key: string) => void;
  setGoal: (key: string, goal: ReadingGoalView | null) => void;
  /** Wipe everything for a (reader, book) pair — used by "Start over". */
  resetKey: (key: string) => void;
  /** Compose the canonical key from its parts. */
  keyFor: (readerId: string, bookId: string) => string;
}

export const useReadingTrackerStore = create<ReadingTrackerState>()(
  persist(
    (set, get) => ({
      currentScreens: {},
      drafts: {},
      goalsByBook: {},

      setScreen: (key, screen) => {
        set({ currentScreens: { ...get().currentScreens, [key]: screen } });
      },

      setDraft: (key, patch) => {
        const drafts = get().drafts;
        const current = drafts[key] ?? EMPTY_DRAFT;
        set({ drafts: { ...drafts, [key]: { ...current, ...patch } } });
      },

      resetDraft: (key) => {
        const drafts = { ...get().drafts };
        delete drafts[key];
        set({ drafts });
      },

      setGoal: (key, goal) => {
        set({ goalsByBook: { ...get().goalsByBook, [key]: goal } });
      },

      resetKey: (key) => {
        const drafts = { ...get().drafts };
        const screens = { ...get().currentScreens };
        const goals = { ...get().goalsByBook };
        delete drafts[key];
        delete screens[key];
        delete goals[key];
        set({ drafts, currentScreens: screens, goalsByBook: goals });
      },

      keyFor: (readerId, bookId) => `${readerId}:${bookId}`,
    }),
    {
      name: 'reading-tracker-store',
      storage: createJSONStorage(() => localStorage),
      // Only persist the wizard scaffolding — server goals are re-fetched
      // on mount so a stale cached `completedDays` doesn't paint the wrong
      // dot count after another device marked a day.
      partialize: (state) => ({
        currentScreens: state.currentScreens,
        drafts: state.drafts,
      }),
      // Bumped to v2 when the "Do you have a reading goal?" screen was
      // removed. Maps any persisted `'goal'` screen to `'bookGoal'` and
      // strips the no-longer-supported `overallGoal` field from drafts so
      // returning users don't land on a missing screen / leak dead state.
      version: 2,
      migrate: (persisted, fromVersion) => {
        if (!persisted || typeof persisted !== 'object') return persisted;
        if (fromVersion >= 2) return persisted;
        const state = persisted as {
          currentScreens?: Record<string, string>;
          drafts?: Record<string, Record<string, unknown>>;
        };
        const screens = state.currentScreens ?? {};
        const remappedScreens: Record<string, WizardScreen> = {};
        for (const [k, v] of Object.entries(screens)) {
          remappedScreens[k] = v === 'goal' ? 'bookGoal' : (v as WizardScreen);
        }
        const drafts = state.drafts ?? {};
        const cleanedDrafts: Record<string, WizardDraft> = {};
        for (const [k, d] of Object.entries(drafts)) {
          // Strip overallGoal; keep goalType and perDay.
          cleanedDrafts[k] = {
            goalType: (d.goalType as GoalType | null) ?? null,
            perDay: typeof d.perDay === 'number' ? d.perDay : 2,
          };
        }
        return { ...state, currentScreens: remappedScreens, drafts: cleanedDrafts };
      },
    },
  ),
);

export { EMPTY_DRAFT };
